import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  SiteMenuSettings,
  SiteMenuSettingsCreateRequest,
  SiteMenuSettingsNotFound,
  SiteMenuSettingsUpdateRequest,
} from '@core/models/site-menu-settings.model';
import { BehaviorSubject, catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { EnvironmentService } from '../environment/environment.service';

@Injectable({
  providedIn: 'root',
})
export class SiteMenuSettingsService {
  private readonly environment = inject(EnvironmentService);
  private readonly http = inject(HttpClient);

  private readonly API_URL = this.environment.getApiUrl();

  // Cache Map to store settings by client_id
  private settingsCache = new Map<number, Observable<SiteMenuSettings | null>>();

  // Subject to notify when settings change for any client
  private settingsUpdated$ = new BehaviorSubject<{
    clientId: number;
    settings: SiteMenuSettings | null;
  } | null>(null);

  /**
   * Get site menu settings for a client (authenticated)
   */
  getSettings(clientId: number): Observable<SiteMenuSettings | SiteMenuSettingsNotFound> {
    return this.http.get<SiteMenuSettings | SiteMenuSettingsNotFound>(
      `${this.API_URL}v1/site-menu-settings/${clientId}`,
    );
  }

  /**
   * Get site menu settings publicly (no authentication)
   */
  getPublicSettings(clientId: number): Observable<SiteMenuSettings | SiteMenuSettingsNotFound> {
    return this.http.get<SiteMenuSettings | SiteMenuSettingsNotFound>(
      `${this.API_URL}v1/site-menu-settings/public/${clientId}`,
    );
  }

  /**
   * Create new site menu settings
   */
  createSettings(data: SiteMenuSettingsCreateRequest): Observable<SiteMenuSettings> {
    return this.http.post<SiteMenuSettings>(`${this.API_URL}v1/site-menu-settings`, data).pipe(
      tap((settings) => {
        if (data.client_id !== null) {
          this.settingsCache.delete(data.client_id);
          this.settingsUpdated$.next({ clientId: data.client_id, settings });
        }
      }),
    );
  }

  /**
   * Update existing site menu settings
   */
  updateSettings(
    clientId: number,
    data: SiteMenuSettingsUpdateRequest,
  ): Observable<SiteMenuSettings> {
    return this.http
      .put<SiteMenuSettings>(`${this.API_URL}v1/site-menu-settings/${clientId}`, data)
      .pipe(
        tap((settings) => {
          // Clear cache AFTER successful update
          this.settingsCache.delete(clientId);
          // Notify subscribers about the change
          this.settingsUpdated$.next({ clientId, settings });
        }),
      );
  }

  /**
   * Get cached site menu settings
   * Uses cache to avoid multiple API calls for the same client
   */
  getCachedSettings(clientId: number): Observable<SiteMenuSettings | null> {
    if (!this.settingsCache.has(clientId)) {
      const request$ = this.getPublicSettings(clientId).pipe(
        map((response) => {
          // Transform SiteMenuSettingsNotFound to null
          if ('setup_required' in response) {
            return null;
          }
          return response;
        }),
        catchError((error) => {
          console.error(`Error loading site menu settings for client ${clientId}:`, error);
          // Remove from cache on error so it can be retried
          this.settingsCache.delete(clientId);
          // Return null instead of throwing to not break the UI
          return of(null);
        }),
        shareReplay(1),
      );
      this.settingsCache.set(clientId, request$);
    }
    return this.settingsCache.get(clientId)!;
  }

  /**
   * Clear cache for a specific client or all clients
   * @param clientId Optional client ID to clear specific cache, omit to clear all
   */
  clearCache(clientId?: number): void {
    if (clientId !== undefined) {
      this.settingsCache.delete(clientId);
    } else {
      this.settingsCache.clear();
    }
  }

  /**
   * Observable that emits when settings are updated for any client
   * Components can subscribe to this to react to changes
   */
  getSettingsUpdates(): Observable<{ clientId: number; settings: SiteMenuSettings | null } | null> {
    return this.settingsUpdated$.asObservable();
  }

  /**
   * Force reload settings for a specific client and notify subscribers
   * @param clientId Client ID to reload
   */
  reloadSettings(clientId: number): void {
    // Clear cache first
    this.settingsCache.delete(clientId);

    // Reload and notify
    this.getCachedSettings(clientId).subscribe({
      next: (settings) => {
        this.settingsUpdated$.next({ clientId, settings });
      },
      error: (error) => {
        console.error(`Error reloading settings for client ${clientId}:`, error);
      },
    });
  }
}
